/**
 * <TutorSessionSettingSheet> — un réglage de la génération, ouvert depuis le champ de la session avec le
 * champion, au live du masteur (template).
 *
 * Toucher une puce de réglage (classe, matière, chapitre, type, durée, compétences) ouvre ce réglage : le
 * formulaire d'aujourd'hui (`<ActivityGenerationForm>`), réduit aux champs du réglage touché — la classe et la
 * matière (`scope`), le chapitre et sa recherche, le type, la durée, les compétences (`skillParts`). Rien de neuf : le même
 * brouillon (`UIActivityGenerationDraft`), les mêmes catalogues, le même prochain contrôle en tête des
 * chapitres. « C'est réglé » la referme ; la puce dit le nouveau réglage.
 *
 * **Sur place** (`inline`) : le même réglage, déplié dans la session au-dessus du champ, sans voile ni feuille
 * — au live, la puce se règle là où elle est (Wajih, live en prod du 03/11 : « les réglages en libellés, qui se
 * modifient directement depuis ces libellés »). Défaut `false` : la feuille.
 *
 * Contrôlée : le brouillon et les catalogues arrivent en props, chaque changement repart.
 */
import React from "react"
import { ScrollView } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import {
    type UIActivityGenerationCatalogs,
    type UIActivityGenerationDraft,
    type UIActivityGenerationField,
} from "../../../ports/primary/ui/types/activityGeneration"
import { type UISchoolAssessment } from "../../../ports/primary/ui/types/schoolAssessment"
import { Button } from "../../atoms/Buttons/Button"
import { Row } from "../../atoms/containers/Row"
import { Text } from "../../atoms/typography/Text"
import { Card } from "../../molecules/Card"
import { Modal } from "../../molecules/Modal"
import { ActivityGenerationForm } from "../../organisms/ActivityGenerationForm"

export type TutorSessionSettingSheetProps = {
    visible: boolean
    onClose: () => void
    /** Ce qu'on règle : « Le chapitre », « Le type », « Plus de réglages ». */
    title: string
    /** Les champs du formulaire montrés. */
    fields: UIActivityGenerationField[]
    draft: UIActivityGenerationDraft
    onDraftChange: (next: UIActivityGenerationDraft) => void
    catalogs: UIActivityGenerationCatalogs
    chaptersLoading?: boolean
    skillPartsLoading?: boolean
    /** Les centres d'intérêt de l'élève : « Personnaliser ». Absents : pas de chip. */
    studentInterests?: string | null
    /** Son prochain contrôle : ses chapitres remontent en tête. */
    nextAssessment?: UISchoolAssessment | null
    /** Déplié sur place, au-dessus du champ, au lieu d'une feuille. Défaut `false`. */
    inline?: boolean
    testID?: string
}

const TEXTS = { done: "C'est réglé" }

export function TutorSessionSettingSheet(props: TutorSessionSettingSheetProps) {
    const {
        visible,
        onClose,
        title,
        fields,
        draft,
        onDraftChange,
        catalogs,
        chaptersLoading,
        skillPartsLoading,
        studentInterests,
        nextAssessment,
        inline = false,
        testID,
    } = props
    const form = (
        <ActivityGenerationForm
            value={draft}
            onChange={onDraftChange}
            catalogs={catalogs}
            fields={fields}
            chaptersLoading={chaptersLoading}
            skillPartsLoading={skillPartsLoading}
            studentInterests={studentInterests}
            nextAssessment={nextAssessment}
            voice="student"
        />
    )
    if (inline) {
        if (!visible) return null
        return (
            <Card background="surface-subtle" elevation="none" p="3" style={styles.inline} testID={testID}>
                <Row justify="between" align="center">
                    <Text size="text-sm" weight="bold" color="text-strong" accessibilityRole="header">
                        {title}
                    </Text>
                    <Button label={TEXTS.done} variant="ghost" size="sm" onPress={onClose} />
                </Row>
                <ScrollView style={styles.inlineForm} nestedScrollEnabled>
                    {form}
                </ScrollView>
            </Card>
        )
    }
    return (
        <Modal
            visible={visible}
            onClose={onClose}
            title={title}
            sizeToContent
            footer={<Button label={TEXTS.done} fullWidth onPress={onClose} />}
            testID={testID}
        >
            {form}
        </Modal>
    )
}

/** Sur place : un encart de la session, borné en hauteur (le chapitre et sa recherche défilent dedans). */
const INLINE_MAX_HEIGHT = 320

const styles = StyleSheet.create((theme) => ({
    inline: { gap: theme.spacing["2"] },
    inlineForm: { maxHeight: INLINE_MAX_HEIGHT },
}))
